import { Component, Inject, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AddCameraWizardModels } from './models/wizard.models';
import { DeviceLocationModel } from '../../pages/location-v2/pages/device/models';
import { shareReplay, Subject, take, tap } from 'rxjs';
import { Camera } from '../../cameras/camera.model';
import { LocationModel } from '../../locations/location.model';
import { CameraAuthState, WizardAuthenticationSnapshot, WizardAuthenticationComponent } from './components/wizard-authentication/wizard-authentication.component';
import { CameraConfigState, WizardConfigurationComponent } from './components/wizard-configuration/wizard-configuration.component';
import { CameraServiceV2 } from '../../services/camera.service';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { SharedActions } from '@states/shared/shared.action-types';
import { finalize } from 'rxjs/operators';
import { CamerasActions } from '@states/cameras/cameras.action-types';
import { TranslateService } from '@ngx-translate/core';
import { ViewChild } from '@angular/core';
import { EdgeSelectors } from '@states/edge/edge.selector-types';
import { EnableEscCloseDirective } from '../../shared/directives/enable-esc-close.directive';

export interface AddCameraWizardData {
    locationId: string;
    locationCore: LocationModel.LocationItem;
    debug?: boolean; // Enable debug mode to skip to configuration stage with mock data
}

@Component({
    selector: 'app-add-camera-wizard',
    templateUrl: './add-camera-wizard.component.html',
    styleUrls: ['./add-camera-wizard.component.scss'],
    standalone: false,
    hostDirectives: [EnableEscCloseDirective],
})
export class AddCameraWizardComponent implements OnInit, OnDestroy {
    @ViewChild(WizardConfigurationComponent) configurationComponent: WizardConfigurationComponent;
    @ViewChild(WizardAuthenticationComponent) authenticationComponent: WizardAuthenticationComponent;

    private destroy$ = new Subject<void>();

    public WizardStep = AddCameraWizardModels.WizardStep;
    public DetectionMethod = AddCameraWizardModels.DetectionMethod;
    public currentStep: AddCameraWizardModels.WizardStep = AddCameraWizardModels.WizardStep.CoreSelection;

    public selectedEdgeId: string | null = null;
    public selectedEdgeIds: string[] = []; // For EdgePickerInline
    public detectionMethod: AddCameraWizardModels.DetectionMethod | null = null;
    public addressRangeData: AddCameraWizardModels.AddressRangeData | null = null;
    public manualIpArr: string[] = [];
    public manualDeviceType: 'camera' | 'nvr' = 'camera';
    public csvImportRows: AddCameraWizardModels.ManualCsvImportRow[] = [];
    public selectedCameras: Camera[] = [];
    // Preserve full auth step state when moving to Configuration and back
    public authSnapshot: WizardAuthenticationSnapshot | null = null;

    // Data passed to Configuration (subset of selected items)
    public selectedCamerasForConfig: Camera[] = [];
    public cameraAuthStatesForConfig: Map<string, CameraAuthState> = new Map();
    public scanProgress: number = 0;
    public isScanning: boolean = false;
    public isCreating: boolean = false;
    public cameraAuthStates: Map<string, CameraAuthState> = new Map();
    public selectedAuthCameraIds: Set<string> = new Set();
    public selectedConfigCameraIds: Set<string> = new Set();
    public maxSelectableCameras: number = 0;

    public steps: AddCameraWizardModels.StepConfig[] = [
        { step: AddCameraWizardModels.WizardStep.CoreSelection, key: 'CORE', label: 'ADD_CAMERA_WIZARD.STEPS.CORE' },
        { step: AddCameraWizardModels.WizardStep.DetectionMethod, key: 'DETECTION', label: 'ADD_CAMERA_WIZARD.STEPS.DETECTION' },
        { step: AddCameraWizardModels.WizardStep.ScanSelection, key: 'SELECTION', label: 'ADD_CAMERA_WIZARD.STEPS.SELECTION' },
        { step: AddCameraWizardModels.WizardStep.Authentication, key: 'AUTHENTICATION', label: 'ADD_CAMERA_WIZARD.STEPS.AUTHENTICATION' },
        { step: AddCameraWizardModels.WizardStep.Configuration, key: 'CONFIGURATION', label: 'ADD_CAMERA_WIZARD.STEPS.CONFIGURATION' },
    ];

    constructor(
        public dialogRef: MatDialogRef<AddCameraWizardComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AddCameraWizardData,
        private cdr: ChangeDetectorRef,
        private cameraServiceV2: CameraServiceV2,
        private store$: Store<AppState>,
        private translateService: TranslateService,
    ) { }

    ngOnInit(): void {
        // Initialize wizard - check for debug mode
        if (this.data?.debug) {
            this.initializeDebugMode();
        }
    }

    /**
     * Initialize debug mode with mock camera data for testing the configuration stage
     */
    private initializeDebugMode(): void {
        // Set a mock edge ID
        this.selectedEdgeId = '664a470acec44a1247646db2';
        this.selectedEdgeIds = [this.selectedEdgeId];

        // Mock camera data based on the real API response
        const mockCameraId = '438c170e-fb55-4fd7-917b-ee69ddea6475';

        const mockConnectionDetails: LocationModel.CameraConnectionDetails = {
            id: mockCameraId,
            mainStreamConnectionData: {
                connectionResponseCode: 200,
                snapshotUrl: 'snapshots/664a470acec44a1247646db2/17654464510700.38438201209534917.jpg',
                snapshotResponseCode: 200,
                extendedStreamData: {
                    averageFps: 15,
                    averageBitrate: 3879,
                    totalKeyFrames: 6,
                    width: 2880,
                    height: 1620,
                    codecName: 'HEVC',
                    profile: 'Main',
                    totalDuration: 5,
                    totalSize: 2488721,
                    totalFrames: 77,
                    averageKeyFrameDistance: 15,
                    pixelFormat: 'yuv420p',
                    realFrameRate: '15/1',
                    hasBframes: false,
                    bitratePerSecond: 3347,
                    maxBitrate: 3476,
                    minBitrate: 3273,
                    averageBitratePerSecond: 3361,
                    codecType: 'Video'
                }
            },
            subStreamConnectionData: {
                connectionResponseCode: 200,
                snapshotUrl: 'snapshots/664a470acec44a1247646db2/17654464586390.44518856821756536.jpg',
                snapshotResponseCode: 200,
                extendedStreamData: {
                    averageFps: 25,
                    averageBitrate: 702,
                    totalKeyFrames: 3,
                    width: 1280,
                    height: 720,
                    codecName: 'H264',
                    profile: 'High',
                    totalDuration: 5,
                    totalSize: 459542,
                    totalFrames: 131,
                    averageKeyFrameDistance: 50,
                    pixelFormat: 'yuv420p',
                    realFrameRate: '25/1',
                    hasBframes: false,
                    bitratePerSecond: 949,
                    maxBitrate: 957,
                    minBitrate: 361,
                    averageBitratePerSecond: 717,
                    codecType: 'Video'
                }
            },
            cameraDocument: {
                username: 'admin',
                password: 'password',
                retentionDays: 30,
                isApiSupported: true,
                networkDetails: {
                    ipAddress: '192.168.100.167',
                    port: 554,
                    macAddress: 'e4:f1:4c:63:89:67',
                    forceTcp: false,
                    secured: false
                },
                mainStream: {
                    main: {
                        port: 554,
                        connectionString: '/media/video1'
                    }
                },
                substreamEnabled: true,
                subStreams: [
                    {
                        main: {
                            port: 554,
                            connectionString: '/media/video2'
                        }
                    }
                ]
            }
        };

        // Create mock camera
        const mockCamera: Camera = {
            id: mockCameraId,
            name: 'Debug Camera 1',
            ipAddress: '192.168.100.167',
            mac: 'e4:f1:4c:63:89:67',
            macAddress: 'e4:f1:4c:63:89:67',
            cameraBrand: 'Lumana' as any,
            onvifCompliant: true,
            username: 'admin',
            password: 'password'
        };

        // Create mock auth state
        const mockAuthState: CameraAuthState = {
            camera: mockCamera,
            status: 'success',
            connectionDetails: mockConnectionDetails
        };

        // Set up the wizard state
        this.selectedCameras = [mockCamera];
        this.cameraAuthStates = new Map([[mockCameraId, mockAuthState]]);
        this.selectedAuthCameraIds = new Set([mockCameraId]);

        // Jump to Configuration step
        this.currentStep = AddCameraWizardModels.WizardStep.Configuration;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public close(): void {
        this.dialogRef.close();
    }

    public onCoreSelected(edgeIds: string[]): void {
        this.selectedEdgeIds = edgeIds;
        if (edgeIds && edgeIds.length > 0) {
            this.selectedEdgeId = edgeIds[0];
            this.store$.dispatch(CamerasActions.setEdgeId({ edgeId: this.selectedEdgeId }));
            const selectEdge$ = this.store$.pipe(select(EdgeSelectors.selectEdgeById(this.selectedEdgeId)));
            selectEdge$
                .pipe(
                    shareReplay(),
                    take(1),
                    tap((edge) => {
                        this.store$.dispatch(CamerasActions.setLocationId({ locationId: edge.locationId }));
                    }),
                )
                .subscribe();
        }
    }

    public saveCore(): void {
        if (this.selectedEdgeId) {
            this.goToNextStep();
        }
    }

    public onDetectionMethodSelected(data: {
        method: AddCameraWizardModels.DetectionMethod;
        addressRange?: AddCameraWizardModels.AddressRangeData;
    } | null): void {
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            if (data) {
                this.detectionMethod = data.method;
                this.addressRangeData = data.addressRange || null;
                this.csvImportRows = [];
                this.selectedCameras = [];
            } else {
                this.detectionMethod = null;
                this.addressRangeData = null;
                this.csvImportRows = [];
                this.selectedCameras = [];
            }
            this.cdr.detectChanges();
        });
    }

    public onCamerasSelected(cameras: Camera[]): void {
        this.selectedCameras = cameras;
    }

    public onCsvImportCompleted(rows: AddCameraWizardModels.ManualCsvImportRow[]): void {
        this.csvImportRows = rows;
    }

    public isManualDiscoveryFlow(): boolean {
        return this.detectionMethod === AddCameraWizardModels.DetectionMethod.Manual
            || this.detectionMethod === AddCameraWizardModels.DetectionMethod.CsvImport;
    }

    public onCameraDetailsReceived(states: Map<string, CameraAuthState>): void {
        this.cameraAuthStates = states;
    }

    public onSelectedCamerasChanged(selectedIds: Set<string>): void {
        this.selectedAuthCameraIds = selectedIds;
    }

    public onConfigSelectionChanged(selectedIds: Set<string>): void {
        this.selectedConfigCameraIds = selectedIds;
    }

    public onMaxSelectableCamerasChange(count: number): void {
        this.maxSelectableCameras = count;
        this.cdr.detectChanges();
    }

    public openConfigureDialog(): void {
        this.configurationComponent?.openConfigureDialog();
    }

    public clearConfigSelection(): void {
        this.selectedConfigCameraIds.clear();
        this.configurationComponent?.clearSelection();
    }

    public onNext(): void {
        if (this.currentStep === AddCameraWizardModels.WizardStep.Authentication) {
            // Snapshot Authentication step so going back keeps all data
            this.authSnapshot = this.authenticationComponent?.getSnapshot?.() || null;

            // Build Configuration inputs from selected items (don't mutate auth state)
            const selectedStates = new Map<string, CameraAuthState>();
            this.selectedAuthCameraIds.forEach(id => {
                const state = this.cameraAuthStates.get(id);
                if (state) selectedStates.set(id, state);
            });
            this.cameraAuthStatesForConfig = selectedStates;
            this.selectedCamerasForConfig = this.selectedCameras.filter(c => this.selectedAuthCameraIds.has(c.id));
            this.goToNextStep();
        } else if (this.currentStep === AddCameraWizardModels.WizardStep.Configuration) {
            this.createCameras();
        } else {
            this.goToNextStep();
        }
    }

    private createCameras(): void {
        const configs = this.configurationComponent?.cameraConfigsArray || [];
        if (configs.length === 0) return;

        this.isCreating = true;

        // Build a single bulk request with all cameras
        const cameras = configs.map(config => ({
            camera: this.buildFullCameraDocument(config),
            defaultSnapshot: config.connectionDetails.mainStreamConnectionData.snapshotUrl,
        }));

        const request: LocationModel.AddCamerasToLocationRequest = {
            edgeId: this.selectedEdgeId!,
            locationId: this.data.locationId,
            cameras,
        };

        this.cameraServiceV2.addCamerasToLocation(request)
            .pipe(
                finalize(() => {
                    this.isCreating = false;
                })
            )
            .subscribe({
                next: (res) => {
                    const locationName = this.data.locationCore?.name || this.translateService.instant('COMMON.LOCATION');
                    const count = res.totalCameras || cameras.length;
                    const summaryMessage = count === 1
                        ? this.translateService.instant('ADD_CAMERA_WIZARD.SUMMARY.BULK_ONE', { location: locationName })
                        : this.translateService.instant('ADD_CAMERA_WIZARD.SUMMARY.BULK_MANY', { count, location: locationName });

                    this.store$.dispatch(SharedActions.showMessage({ info: summaryMessage }));
                    this.dialogRef.close(true);
                },
                error: (err) => {
                    const errorMsg = err?.error?.message || err?.message || this.translateService.instant('TOASTS.UNKNOWN_ERROR');
                    this.store$.dispatch(SharedActions.showMessage({ info: errorMsg }));
                }
            });
    }

    /**
     * Builds a complete camera document from MiniCameraDocument + original camera data + defaults.
     * This replicates the data structure that the camera-activate-dialog flow produces.
     */
    private buildFullCameraDocument(config: CameraConfigState): any {
        const miniDoc = config.connectionDetails.cameraDocument;
        const originalCamera = config.originalCamera;
        const mainStreamData = config.connectionDetails.mainStreamConnectionData;
        const subStreamData = config.connectionDetails.subStreamConnectionData;

        // Get extended stream data for width/height/fps
        const mainExtended = mainStreamData?.extendedStreamData;
        const subExtended = subStreamData?.extendedStreamData;

        // Build default HLS configuration based on stream resolution
        const buildHlsConfig = (width?: number, height?: number) => ({
            hlsFragmentDuration: 1070,
            hlsMaxLiveFragments: 3,
            hlsMaxStoredFragments: 6,
            hlsLiveDirect: true,
            hlsHQScaledFragmentDuration: 1070,
            hlsHQScaledMaxLiveFragments: 3,
            hlsHQScaledMaxStoredFragments: 6,
            hlsHQScaledWidth: 1280,
            hlsHQScaledHeight: -2,
            hlsHQScaledBitrateBps: 5000000,
            hlsHQScaledRateControlType: 'vbr',
            hlsHQScaledPeakBitrateBps: 7500000,
            hlsHQScaledIFrameInterval: 30,
            hlsHQScaledPresetLevel: 'fast',
            hlsSQScaledFragmentDuration: 1000,
            hlsSQScaledMaxLiveFragments: 3,
            hlsSQScaledMaxStoredFragments: 6,
            hlsSQScaledWidth: 640,
            hlsSQScaledHeight: -2,
            hlsSQScaledBitrateBps: 1000000,
            hlsSQScaledRateControlType: 'vbr',
            hlsSQScaledPeakBitrateBps: 1500000,
            hlsSQScaledIFrameInterval: 15,
            hlsSQScaledPresetLevel: 'fast',
            hlsSQliveScaleFragmentDurationIFrame: true,
            hlsHQliveScaleFragmentDurationIFrame: true
        });

        // Build full stream configuration
        const buildFullStreamConfig = (
            streamMain: any,
            extended?: any,
            audioEnabled?: boolean
        ) => {
            const width = extended?.width || 1920;
            const height = extended?.height || 1080;

            return {
                thumbnailsEnabled: true,
                thumbnailsDuration: 2000,
                motionVectorsEnabled: true,
                motionVectorsDuration: 20000,
                videoAnalyticsEnabled: true,
                videoAnalyticsMinSensativity: 0,
                videoAnalyticsMaxFps: 0,
                audioAnalyticsEnabled: false,
                audioInputEnabled: audioEnabled || false,
                skipPing: false,
                storageMaxHours: 0,
                cloudRetentionMaxHours: 0,
                main: {
                    inputType: 0,
                    protocol: 0,
                    port: streamMain?.port || 554,
                    connectionString: streamMain?.connectionString || '',
                    width,
                    height,
                    fps: extended?.averageFps || null,
                    analyzationDuration: null,
                    probeSize: null,
                    ...buildHlsConfig(width, height)
                },
                mainScale: {
                    inputType: 0,
                    width: 1280,
                    height: 720,
                    fps: null,
                    ...buildHlsConfig(1280, 720)
                },
                history: {
                    inputType: 6,
                    width,
                    height,
                    fps: null,
                    ...buildHlsConfig(width, height)
                },
                profile: null
            };
        };

        // Fallback stream info from user-entered credentials for unauthenticated cameras
        const fallbackCreds = this.authSnapshot?.credentialsArr?.[0];
        const fallbackMainStreamMain = !miniDoc?.mainStream?.main ? {
            port: Number(fallbackCreds?.port) || 554,
            connectionString: fallbackCreds?.urls?.[0]?.mainUrl || '/',
        } : undefined;

        // Build full mainStream
        const fullMainStream = buildFullStreamConfig(
            miniDoc?.mainStream?.main || fallbackMainStreamMain,
            mainExtended,
            config.audioMainStreamEnabled
        );

        // Build full subStreams
        const fallbackSubStreamMain = !miniDoc?.subStreams && fallbackCreds?.subStream && fallbackCreds?.urls?.[0]?.subUrl ? {
            port: Number(fallbackCreds?.port) || 554,
            connectionString: fallbackCreds?.urls?.[0]?.subUrl,
        } : undefined;
        const fullSubStreams = miniDoc?.subStreams?.map((sub, index) => {
            const subExt = index === 0 ? subExtended : undefined;
            return buildFullStreamConfig(
                sub?.main,
                subExt,
                config.audioSubStreamEnabled
            );
        }) || (fallbackSubStreamMain ? [buildFullStreamConfig(fallbackSubStreamMain, subExtended, config.audioSubStreamEnabled)] : []);

        // Build camera device info from ONVIF data
        const cameraDeviceInfo = miniDoc?.cameraDeviceInfo || {
            xaddrs: originalCamera?.onvifData?.xaddrs || [],
            name: originalCamera?.onvifData?.name || '',
            scopes: originalCamera?.onvifData?.scopes || []
        };

        // Build the complete camera document
        const camera: any = {
            // From MiniCameraDocument, falling back to user-entered credentials
            username: miniDoc?.username || fallbackCreds?.username || '',
            password: miniDoc?.password || fallbackCreds?.password || '',
            retentionDays: miniDoc?.retentionDays || 30,
            isApiSupported: miniDoc?.isApiSupported ?? true,

            // Network details with fallback from original camera for failed auth
            networkDetails: {
                ipAddress: miniDoc?.networkDetails?.ipAddress || originalCamera?.ipAddress,
                port: miniDoc?.networkDetails?.port || Number(originalCamera?.port) || 554,
                macAddress: originalCamera?.macAddress || originalCamera?.mac || miniDoc?.networkDetails?.macAddress,
                forceTcp: false,
                secured: miniDoc?.networkDetails?.secured ?? false,
                status: 'connected'
            },

            // From original camera
            cameraBrand: originalCamera?.cameraBrand || 'Unknown',
            onvifCompliant: miniDoc?.onvifCompliant ?? originalCamera?.onvifCompliant ?? false,

            // Full stream configurations
            mainStream: fullMainStream,
            substreamEnabled: miniDoc?.substreamEnabled ?? false,
            subStreams: fullSubStreams,

            // Camera device info
            cameraDeviceInfo,

            // PTZ support
            ptzSupport: miniDoc?.ptzSupport || false,

            // User configuration
            name: config.name,
            cameraType: config.cameraType,

            // Auto configure settings
            autoConfigureMainstream: config.autoConfigure,
            autoConfigureSubstream: config.autoConfigure && miniDoc?.substreamEnabled,
            autoConfigure: config.autoConfigure,

            // Storage settings
            storageMaxDays: config.storageMaxDays,
            storageMaxGB: 0,
            storageStream: {
                storageFromSubstream: config.storageFromSubstream ? 1 : 0,
                storageSubstreamIndex: 0
            },
            smartStorage: {
                enabled: config.smartStorageEnabled,
                preEventDuration: 20,
                postEventDuration: 20
            },

            // Default values
            snapshotRetentionDays: 3,
            storageSchedule: [],
            integration: {
                isIntegrationEnabled: false
            },
            cloudStorage: {
                enabled: false,
                backupStorage: false,
                backupAlerts: false,
                backupEventTypes: false,
                footageSchedule: false,
                uploadSchedule: false
            },
            hqConfig: {
                liveKit: {
                    isPassthrough: true
                }
            }
        };

        // Add cameraId if available (from MiniCameraDocument - may exist at runtime)
        if ((miniDoc as any)?.cameraId) {
            camera.cameraId = (miniDoc as any).cameraId;
        }

        // Add profiles if available
        if ((miniDoc as any)?.profiles) {
            camera.profiles = (miniDoc as any).profiles;
        }

        const genetecGuid = originalCamera?.genetecGuid;
        if (genetecGuid) {
            const genetecCred = (this.authSnapshot?.credentialsArr || [])
                .find(c => c?.nvrVendor === WizardAuthenticationComponent.GENETEC_VENDOR);
            const serverIp = genetecCred?.genetecServerIp;
            if (serverIp) {
                const port = genetecCred?.port || WizardAuthenticationComponent.GENETEC_MEDIA_GATEWAY_PORT;
                const protocol = genetecCred?.protocol ?? LocationModel.RtspProtocol.SECURED;
                camera.cameraBrand = 'Unknown';
                camera.networkDetails = {
                    ipAddress: serverIp,
                    port,
                    forceTcp: protocol === LocationModel.RtspProtocol.TCP,
                    secured: protocol === LocationModel.RtspProtocol.SECURED,
                    status: 'connected',
                };
                camera.mainStream.main.port = port;
                camera.mainStream.main.protocol = 0;
                camera.mainStream.main.connectionString = WizardAuthenticationComponent.buildGenetecStreamPath(genetecGuid);
                if (genetecCred?.subStream) {
                    camera.substreamEnabled = true;
                    camera.subStreams = [buildFullStreamConfig(
                        { port, connectionString: WizardAuthenticationComponent.buildGenetecSubStreamPath(genetecGuid) },
                        undefined,
                        config.audioSubStreamEnabled,
                    )];
                } else {
                    camera.substreamEnabled = false;
                    camera.subStreams = [];
                }
                const webSdk = genetecCred?.genetecWebSdk;
                camera.genetec = {
                    genetecGuid,
                    baseUrl: webSdk?.ip || serverIp,
                    username: webSdk?.username || camera.username,
                    password: webSdk?.password || camera.password,
                };
            }
        }

        return camera;
    }

    public getNextButtonText(): string {
        if (this.currentStep === AddCameraWizardModels.WizardStep.Configuration) {
            return 'ADD_CAMERA_WIZARD.FOOTER.FINISH_SETUP';
        }
        if (this.currentStep === AddCameraWizardModels.WizardStep.Authentication) {
            const selectedCount = this.selectedAuthCameraIds.size;
            return selectedCount > 0 ? 'ADD_CAMERA_WIZARD.FOOTER.ADD_SELECTED_CAMERAS' : 'COMMON.NEXT';
        }
        return 'COMMON.NEXT';
    }

    public getNextButtonTextParams(): any {
        if (this.currentStep === AddCameraWizardModels.WizardStep.Authentication) {
            return { count: this.selectedAuthCameraIds.size };
        }
        return {};
    }

    public onBack(): void {
        if (this.currentStep > AddCameraWizardModels.WizardStep.CoreSelection) {
            this.currentStep--;
        }
    }

    public goToNextStep(): void {
        // Allow progression through all steps including Configuration
        if (this.currentStep < AddCameraWizardModels.WizardStep.Configuration) {
            this.currentStep++;
        }
    }

    public canGoBack(): boolean {
        return this.currentStep > AddCameraWizardModels.WizardStep.CoreSelection && !this.isScanning;
    }

    public canGoNext(): boolean {
        // Validation logic for Next button
        switch (this.currentStep) {
            case AddCameraWizardModels.WizardStep.CoreSelection:
                return !!this.selectedEdgeId;
            case AddCameraWizardModels.WizardStep.DetectionMethod:
                return !!this.detectionMethod;
            case AddCameraWizardModels.WizardStep.ScanSelection:
                return this.selectedCameras.length > 0;
            case AddCameraWizardModels.WizardStep.Authentication:
                return this.selectedAuthCameraIds.size > 0;
            case AddCameraWizardModels.WizardStep.Configuration:
                // Always allow finish on Configuration step
                return !this.isCreating;
            default:
                return false;
        }
    }

    public getStepTitle(): string {
        switch (this.currentStep) {
            case AddCameraWizardModels.WizardStep.CoreSelection:
                return 'ADD_CAMERA_WIZARD.TITLE.CORE';
            case AddCameraWizardModels.WizardStep.DetectionMethod:
                return 'ADD_CAMERA_WIZARD.TITLE.DETECTION';
            case AddCameraWizardModels.WizardStep.ScanSelection:
                return this.detectionMethod === AddCameraWizardModels.DetectionMethod.CsvImport
                    ? 'ADD_CAMERA_WIZARD.TITLE.CSV_IMPORT'
                    : this.isScanning ? 'ADD_CAMERA_WIZARD.TITLE.SCANNING' : 'ADD_CAMERA_WIZARD.TITLE.SELECTION';
            case AddCameraWizardModels.WizardStep.Authentication:
                return 'ADD_CAMERA_WIZARD.TITLE.AUTHENTICATION';
            case AddCameraWizardModels.WizardStep.Configuration:
                return '';
            default:
                return '';
        }
    }

    // Get the total count of cameras to be added for footer display
    public getCamerasToAddCount(): number {
        if (this.currentStep === AddCameraWizardModels.WizardStep.Configuration) {
            return this.cameraAuthStatesForConfig.size;
        }
        return 0;
    }
}
